import React, { useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { forgotPassword } from '../services/auth'
import { theme } from '../styles/theme'

const Container = styled.div`
  position: relative;
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xl};
  background: linear-gradient(160deg, ${theme.colors.background} 0%, ${theme.colors.light} 45%, ${theme.colors.lightHover} 100%);
  overflow: hidden;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 420px;
    height: 420px;
    border-radius: 50%;
    opacity: 0.35;
    z-index: 0;
  }

  &::before {
    top: -140px;
    right: -140px;
    background: radial-gradient(circle, ${theme.colors.honeycomb} 0%, transparent 70%);
  }

  &::after {
    bottom: -160px;
    left: -160px;
    background: radial-gradient(circle, ${theme.colors.primaryLight} 0%, transparent 70%);
  }
`

const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 460px;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.xxlarge};
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid ${theme.colors.borderLight};
  box-shadow: ${theme.shadows.xl};
  backdrop-filter: blur(10px);
`

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
`

const LogoMark = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${theme.borderRadius.large};
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.white};
  font-size: ${theme.typography.fontSize.large};
  font-weight: ${theme.typography.fontWeight.bold};
  letter-spacing: 1px;
  box-shadow: 0 10px 24px rgba(246, 180, 0, 0.35);
`

const Title = styled.h1`
  font-size: ${theme.typography.fontSize.xxlarge};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text};
  text-align: center;
`

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.small};
  color: ${theme.colors.textSecondary};
  text-align: center;
  line-height: 1.5;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`

const Label = styled.label`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textSecondary};
  letter-spacing: 0.3px;
  text-transform: uppercase;
`

const Input = styled.input`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.large};
  font-size: ${theme.typography.fontSize.medium};
  background: ${theme.colors.backgroundSecondary};
  color: ${theme.colors.text};
  transition: ${theme.transitions.normal};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 4px rgba(246, 180, 0, 0.15);
    background: ${theme.colors.white};
  }

  &::placeholder {
    color: ${theme.colors.textTertiary};
  }
`

const Button = styled.button<{ disabled?: boolean; isLoading?: boolean }>`
  margin-top: ${theme.spacing.xs};
  padding: ${theme.spacing.md};
  background: ${props =>
    props.disabled || props.isLoading
      ? theme.colors.border
      : `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%)`};
  color: ${theme.colors.white};
  border: none;
  border-radius: ${theme.borderRadius.large};
  font-size: ${theme.typography.fontSize.medium};
  font-weight: ${theme.typography.fontWeight.bold};
  cursor: ${props => (props.disabled || props.isLoading ? 'not-allowed' : 'pointer')};
  transition: ${theme.transitions.normal};
  box-shadow: 0 12px 24px rgba(209, 138, 0, 0.2);

  &:hover {
    transform: ${props => (props.disabled || props.isLoading ? 'none' : 'translateY(-1px)')};
    box-shadow: ${props => (props.disabled || props.isLoading ? 'none' : '0 16px 28px rgba(209, 138, 0, 0.28)')};
  }
`

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: ${theme.spacing.md};
  padding: 0;
  border: none;
  background: none;
  color: ${theme.colors.primaryDark};
  font-size: ${theme.typography.fontSize.small};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  align-self: center;
  transition: ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.primary};
  }
`

const ErrorMessage = styled.div`
  color: ${theme.colors.danger};
  font-size: ${theme.typography.fontSize.small};
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.medium};
`

const SuccessMessage = styled.div`
  color: ${theme.colors.success};
  font-size: ${theme.typography.fontSize.small};
  background: rgba(16, 185, 129, 0.08);
  border: 1px solid rgba(16, 185, 129, 0.2);
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.medium};
  line-height: 1.5;
`

/**
 * Forgot password screen component
 * @returns Forgot password screen
 */
function ForgotPasswordScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!email.trim()) {
      setError(t('auth.emailRequired'))
      return
    }

    try {
      setIsSubmitting(true)
      await forgotPassword(email.trim())
      setSuccess(t('auth.resetLinkSent'))
    } catch (err: any) {
      setError(err.message || t('auth.resetFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Container>
      <Card>
        <CardHeader>
          <LogoMark>🐝</LogoMark>
          <Title>{t('auth.forgotPassword')}</Title>
          <Subtitle>{t('auth.forgotPasswordMessage')}</Subtitle>
        </CardHeader>

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={t('auth.email')}
              autoComplete="email"
              required
            />
          </InputGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <Button type="submit" disabled={isSubmitting} isLoading={isSubmitting}>
            {isSubmitting ? t('common.loading') : t('auth.sendResetLink')}
          </Button>

          <BackLink type="button" onClick={() => navigate('/login')}>
            {t('auth.backToLoginArrow')}
          </BackLink>
        </Form>
      </Card>
    </Container>
  )
}

export default ForgotPasswordScreen
